import React from "react";
import { useEffect } from "react";
import { withAuthProvider } from "../auth";
import { initTrackingContext, initTracking } from "./tracking";
import { jobsBySalesforceContactId } from "../../queries";
import { GraphQLQuery } from "../../features/common/functions/graphql";
import { initIndex } from "../algolia";

const buildCustomAlgoliaQuery = ({
  queryParams,
  created,
  hitsPerPage = 10,
}) => {
  return {
    hitsPerPage,
    page: 0,
    attributesToRetrieve: [
      "jobId",
      "jobTitle",
      "created",
      "facilities",
      "job_category",
      "base_compensation_min",
      "current_step",
      "applicant_group",
      "offices",
      "facilities",
      "job_category",
      "employment_type",
      "years_experience",
      "submission_deadline",
      "base_compensation_max",
      "additional_pga_education",
      "pay_schedule",
      "education",
      "apply_via",
      "experience_required",
    ],
    responseFields: "*",
    filters: `(annual_compensation_min.value >= 0.00 OR annual_compensation_max.value >= 0.00) AND submission_deadline > ${
      Date.now() / 1000
    } AND (current_step:"complete" OR current_step:"Complete")`,
  };
};

const buildSingleAlgoliaQuery = (Id) => {
  return {
    hitsPerPage: 150,
    page: 0,
    attributesToRetrieve: [
      "jobId",
      "jobTitle",
      "created",
      "facilities",
      "job_category",
      "base_compensation_min",
      "current_step",
      "applicant_group",
      "offices",
      "facilities",
      "job_category",
      "employment_type",
      "years_experience",
      "submission_deadline",
      "base_compensation",
      "base_compensation_max",
      "additional_pga_education",
      "pay_schedule",
      "education",
      "apply_via",
      "experience_required",
    ],
    responseFields: "*",
    filters: `jobId: "${Id}"`,
  };
};

export const getAllJobs = async () => {
  const jobsIndex = initIndex(
    process.env.REACT_APP_ALGOLIA_APP_ID,
    process.env.REACT_APP_ALGOLIA_API_KEY,
    process.env.REACT_APP_ALGOLIA_INDEX_NAME,
  );

  const algoliaQuery = buildCustomAlgoliaQuery({
    queryParams: {},
    hitsPerPage: 150,
  });
  const { hits: allJobs } = algoliaQuery
    ? await jobsIndex.search(algoliaQuery)
    : [];

  return allJobs;
};

export const getSingleJob = async (jobId) => {
  const jobsIndex = initIndex(
    process.env.REACT_APP_ALGOLIA_APP_ID,
    process.env.REACT_APP_ALGOLIA_API_KEY,
    process.env.REACT_APP_ALGOLIA_INDEX_NAME,
  );

  const algoliaQuery = buildSingleAlgoliaQuery(jobId);
  const { hits: singleJob } = algoliaQuery
    ? await jobsIndex.search(algoliaQuery)
    : [];

  return singleJob;
};

function extractUserTraits(me) {
  return {
    id: me ? me.id || null : null,
    email: me ? me.primaryEmail || null : null,
    type: me ? me.type || null : null,
    firstName: me ? me.firstName || null : null,
    lastName: me ? me.lastName || null : null,
    displayName: me ? me.displayName || null : null,
    role: me ? me.role || null : null,
    class: me ? me.class || null : null,
    universalId: me ? me.universalId || null : null,
    salesforceId: me ? me.salesforceId || null : null,
    canSeePgaProJob: me ? me.canSeePgaProJob || null : null,
  };
}

const runIdentifyIfTraitsChanged = (me) => {
  if (!me.id) return false;

  const traits = extractUserTraits(me);
  const storedTraits = JSON.parse(
    localStorage.getItem("last_identified_traits") || "{}",
  );

  const traitsChanged = JSON.stringify(traits) !== JSON.stringify(storedTraits);

  if (traitsChanged) {
    localStorage.setItem("last_identified_traits", JSON.stringify(traits));
  }

  return traitsChanged;
};

const SegmentWrapperInner = ({ isLoggedIn, me, jobsData }) => {
  useEffect(() => {
    if (typeof window === "undefined" || !window.analytics.identify) return;

    const userId = me ? me.externalId || me.id : null;

    if (isLoggedIn && userId) {
      const previousUserId = sessionStorage.getItem("segment_userId");

      if (runIdentifyIfTraitsChanged(me) || previousUserId !== userId) {
        window.analytics.identify(userId, {
          ...me,
        });
        sessionStorage.setItem("segment_userId", userId);
      }
    } else if (!isLoggedIn) {
      sessionStorage.setItem("segment_userId", "anonymous");
      localStorage.setItem("last_identified_traits", JSON.stringify("{}"));
      window.analytics.reset();
    }

    initTrackingContext({
      user: isLoggedIn && me ? { id: userId, ...me } : null,
      isLoggedIn,
      jobsData,
    });

    // Add a small delay to ensure OneTrust has a chance to load first
    const initTimeout = setTimeout(() => {
      try {
        initTracking();
      } catch (error) {
        console.error("Error initializing tracking:", error);
      }
    }, 1000); // 1 second delay

    return () => {
      clearTimeout(initTimeout);
    };
  }, [isLoggedIn, me, jobsData]);
  return null;
};

const SegmentWrapper = (props) => {
  const { isLoggedIn, me, contactId } = props;

  // Don't execute GraphQL query if user is not logged in.
  if (!isLoggedIn) {
    return (
      <SegmentWrapperInner isLoggedIn={isLoggedIn} me={me} jobsData={null} />
    );
  }

  return (
    <GraphQLQuery
      query={jobsBySalesforceContactId}
      variables={{
        input: {
          contactId: contactId || (me && me.salesforceId),
        },
      }}
      fetchPolicy="network-only"
    >
      {({ data: jobsData }) => (
        <SegmentWrapperInner
          isLoggedIn={isLoggedIn}
          me={me}
          jobsData={jobsData}
        />
      )}
    </GraphQLQuery>
  );
};

export default withAuthProvider(SegmentWrapper);
